import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PropTypes } from 'prop-types'
import Box from '@mui/material/Box'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import Divider from '@mui/material/Divider'
import TableRow, { tableRowClasses } from '@mui/material/TableRow'
import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import SubHeader from '../Headers/SubHeader'
import { getStreetImage } from '../../../streets/image'
import { modalTiers } from '../../config/generalInputs.config'
import {
  AverageScoringModal,
  WeakestLinkModal
} from '../CriteriaSelection/Scoring'
import CustomButton from '../CustomButton'
import config from '../../config/config'
import { SectionTitle, SubSectionTitle } from '../ProjectInputs/ProjectInputs'
import { setActiveStep } from '../../slices/mainTool'
import { setDownloadDataUrl } from '../../slices/appSlice'
import ScoringCriteriaTable from './ScoringCriteriaTable'
import SafetyCounterMeasures from './SafetyCounterMeasures'
import WeakestLinkVariables from './WeakestLinkVariables'
import './ScoringSummary.scss'

export const StyledTableHeadingCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.text.heading
  },
  [`&.${tableCellClasses.root}`]: {
    fontSize: 18,
    fontWeight: 600
  }
}))
export const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.root}`]: {
    fontSize: 16
  }
}))

export const Wrapper = ({ children }) => {
  return (
    <Box sx={{ flexGrow: 1, p: '2em', overflow: 'scroll' }}>{children}</Box>
  )
}

export const StyledTableRow = styled(TableRow)(() => ({
  [`&.${tableRowClasses.root}`]: {
    border: '0.1px solid lightgrey'
  }
}))

export default function ScoringSummary() {
  const { street } = useSelector((state) => state)
  const { downloadDataUrl } = useSelector((state) => state.appSlice)
  const imageCanvas = useRef()
  const dispatch = useDispatch()

  useEffect(() => {
    imageCanvas.current = getStreetImage(street, false, true, true)
    const dataUrl = imageCanvas.current.toDataURL('image/png')
    dispatch(setDownloadDataUrl(dataUrl))
  }, [])

  function handleNext() {
    dispatch(setActiveStep(3))
  }

  return (
    <Wrapper>
      <Box sx={{ height: '70px', position: 'relative' }}>
        <SubHeader top={0} left={0} marginTop={0} />
      </Box>
      <Typography
        variant="h2"
        sx={{
          marginBottom: 2,
          color: config.themeConfig.contrastColor,
          textAlign: 'center'
        }}
      >
        Scoring Summary
      </Typography>

      <Box
        sx={{
          width: '100%',
          backgroundColor: '#EFEFEF',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          marginBottom: '2em',
          height: '500px'
        }}
      >
        <img
          src={downloadDataUrl}
          style={{ objectFit: 'contain', width: '100%', height: '100%' }}
        />
      </Box>

      <Box
        sx={{
          gridTemplateColumns: 'repeat( auto-fit, minmax(600px, 1fr) )',
          display: 'grid',
          gap: '15px',
          // backgroundColor: '#EFEFEF',
          padding: '3em'
        }}
      >
        <Box>
          {' '}
          <Typography
            variant="h2"
            sx={{ marginBottom: 2, textAlign: 'center' }}
          >
            Average Score
          </Typography>
          <SubSectionTitle
            fontWeight={400}
            fontSize={14}
            marginBottom="2em"
            textAlign="center"
          >
            Each street user’s average score of all their elements determines
            their overall score.
          </SubSectionTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              height: '350px',
              gap: '5px',
              width: '100%',
              mt: '1em',
              mb: '1.5em'
            }}
          >
            {modalTiers.map((tier) => {
              return (
                <AverageScoringModal
                  key={`${tier.name}-scoring`}
                  name={tier.name}
                  tierId={tier.id}
                />
              )
            })}
          </Box>
        </Box>

        <Box className="borderLeftBox" sx={{ borderLeft: '1px solid #D2D2D2' }}>
          {' '}
          <Typography
            variant="h2"
            sx={{ marginBottom: 2, textAlign: 'center' }}
          >
            Weakest Link Score
          </Typography>
          <SubSectionTitle
            fontWeight={400}
            fontSize={14}
            marginBottom="2em"
            textAlign="center"
          >
            Each street user’s lowest-scoring element determines their overall
            score.
          </SubSectionTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              height: '350px',
              gap: '5px',
              width: '100%',
              mt: '1em',
              mb: '1.5em',
              ml: '2em'
            }}
          >
            {modalTiers.map((tier) => {
              return (
                <WeakestLinkModal
                  key={`${tier.name}-scoring`}
                  name={tier.name}
                  tierId={tier.id}
                />
              )
            })}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          gridTemplateColumns: 'repeat( auto-fit, minmax(600px, 1fr) )',
          display: 'grid',
          gap: '10px',
          width: '100%',
          marginTop: '2em'
        }}
      >
        {/* <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}
        >
          <Box sx={{ pl: '2.5em' }}>
            <SectionTitle>What is Average Score ?</SectionTitle>
            <SubSectionTitle fontWeight={400} fontSize={14}>
              Each street user’s average score of all their elements determines
              their overall score.
            </SubSectionTitle>
          </Box>

          <Box sx={{ pl: '2.5em' }}>
            {' '}
            <SectionTitle>What is Weakest Link ?</SectionTitle>
            <SubSectionTitle fontWeight={400} fontSize={14}>
              Each street user’s lowest-scoring element determines their overall
              score.
            </SubSectionTitle>
          </Box>
        </Box> */}

        <Box
          sx={{
            // backgroundColor: '#EFEFEF',
            padding: '3em',
            alignSelf: 'flex-end'
          }}
        >
          {' '}
          <Typography
            variant="h2"
            sx={{ marginBottom: 2, textAlign: 'center' }}
          >
            Which Elements Are the Weakest Links?
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              // height: '350px',
              gap: '10px',
              width: '100%',
              mt: '4em',
              mb: '1.5em'
            }}
          >
            {modalTiers.map((tier) => {
              return (
                <WeakestLinkVariables
                  key={`${tier.name}-scoring`}
                  name={tier.name}
                  tierId={tier.id}
                />
              )
            })}
          </Box>
        </Box>
      </Box>
      <Divider
        sx={{
          width: '60%',
          margin: 'auto',
          marginTop: '30px',
          marginBottom: '20px'
        }}
      />
      <Box>
        <Typography
          variant="h2"
          sx={{ marginBottom: 2, color: config.themeConfig.contrastColor }}
        >
          Understanding Your Score
        </Typography>
        <SubSectionTitle fontWeight={400} fontSize={14} marginBottom="1em">
          Land use contexts with lots of activity and lots of different types of
          road users, like Main Street, Urban or Walkable Thoroughfares, Small
          Town Hamlet or Village Commercial, and Suburban Corridors, need a
          score of 1 or 2 to create a safe and welcoming environment for all
          street users. Rural environments with less activity need a score of 1,
          2, or 3 to satisfy this objective.
        </SubSectionTitle>
        <ScoringCriteriaTable />
      </Box>
      {/* <Box sx={{ p: '2.5em' }}>
        <SectionTitle>What does this mean ?</SectionTitle>
      </Box> */}
      <Divider
        sx={{
          width: '60%',
          margin: 'auto',
          marginBottom: '20px'
        }}
      />
      <Box
        sx={{
          gridTemplateColumns: '50% 50%',
          display: 'grid',
          gap: '10px',
          width: '100%'
        }}
      >
        <SafetyCounterMeasures />
        <Box p="3em">
          <SectionTitle>
            About FHWA’s Proven Safety Countermeasures
          </SectionTitle>
          <SubSectionTitle fontWeight={400} fontSize={14}>
            FHWA’s Proven Safety Countermeasures are effective strategies to
            reduce roadway fatalities and serious injuries. These strategies are
            designed for all road users and all kinds of roads—from rural to
            urban, from high-volume to less traveled, and everything in between.
            Each countermeasure addresses at least one safety focus area—speed
            management, intersections, roadway departures, or
            pedestrians/bicyclists—while others are crosscutting strategies that
            address multiple safety focus areas. To learn more about FHWA’s
            Proven Safety Countermeasures, visit:{' '}
            <a
              href="https://highways.dot.gov/safety/proven-safety-countermeasures"
              target="blank"
            >
              https://highways.dot.gov/safety/proven-safety-countermeasures
            </a>
          </SubSectionTitle>
          <SectionTitle marginTop="1em">
            For More Information about the Multimodal Tool
          </SectionTitle>
          <SubSectionTitle fontWeight={400} fontSize={14}>
            Visit{' '}
            <a href="https://www.semcog.org/mmtool" target="blank">
              https://www.semcog.org/mmtool
            </a>{' '}
            to learn more about the Multimodal Tool, view the tool user guide,
            and contact SEMCOG about the tool.
          </SubSectionTitle>
        </Box>
      </Box>
      <CustomButton
        handleNext={handleNext}
        showDisabledTooltip={true}
        btnLabel="Generate PDF Report"
      />
    </Wrapper>
  )
}
Wrapper.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
}
